import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  FaBook,
  FaFire,
  FaGraduationCap,
  FaHome,
  FaPlay,
  FaSpinner,
  FaTrophy,
  FaUser
} from 'react-icons/fa';

const QUOTE_FALLBACKS = [
  { quote: 'Ujuzi hujengwa kila siku kwa hatua ndogo zenye uthabiti.', author: 'Fundisha' },
  { quote: 'Learn deeply, then teach boldly.', author: 'Fundisha' },
  { quote: 'Consistency is your quiet superpower.', author: 'Fundisha' }
];

const ProgressRing = ({ progress }) => {
  const size = 80;
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clampedProgress = Math.max(0, Math.min(100, progress || 0));
  const offset = circumference - (clampedProgress / 100) * circumference;

  return (
    <div className="fundisha-ring" aria-label={`Course progress: ${clampedProgress}%`}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(242, 237, 228, 0.2)"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#f5a623"
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span className="fundisha-ring-label">{clampedProgress}%</span>
    </div>
  );
};

const Dashboard = () => {
  const { user, loading, enrolledCourses, fetchUserCourses } = useAuth();
  const navigate = useNavigate();
  const [quote, setQuote] = useState({ quote: 'Loading your daily spark...', author: 'Fundisha' });
  const [streak, setStreak] = useState(1);
  const [activeNav, setActiveNav] = useState('dashboard');

  const completedCourses = useMemo(
    () => enrolledCourses.filter((course) => course.progress === 100),
    [enrolledCourses]
  );

  const inProgressCourses = useMemo(
    () => enrolledCourses.filter((course) => course.progress > 0 && course.progress < 100),
    [enrolledCourses]
  );

  const userDisplayName = useMemo(() => {
    if (user?.name && user.name.trim()) {
      return user.name;
    }

    if (user?.email) {
      return user.email.split('@')[0];
    }

    return 'Learner';
  }, [user]);

  const spotlightCourse = useMemo(() => {
    if (inProgressCourses.length > 0) {
      return inProgressCourses[0];
    }
    return enrolledCourses[0] || null;
  }, [enrolledCourses, inProgressCourses]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth', { replace: true });
    }
  }, [loading, navigate, user]);

  useEffect(() => {
    if (user) {
      fetchUserCourses();
    }
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const cachedQuote = localStorage.getItem(`fundisha-quote-${today}`);

    if (cachedQuote) {
      setQuote(JSON.parse(cachedQuote));
      return;
    }

    const fetchQuote = async () => {
      try {
        const response = await fetch('https://dummyjson.com/quotes/random');
        const data = await response.json();
        const nextQuote = {
          quote: data.quote,
          author: data.author || 'Unknown'
        };

        setQuote(nextQuote);
        localStorage.setItem(`fundisha-quote-${today}`, JSON.stringify(nextQuote));
      } catch (error) {
        const fallback = QUOTE_FALLBACKS[new Date().getDate() % QUOTE_FALLBACKS.length];
        setQuote(fallback);
      }
    };

    fetchQuote();
  }, []);

  useEffect(() => {
    if (!user?._id && !user?.id) {
      return;
    }

    const userId = user._id || user.id;
    const key = `fundisha-streak-${userId}`;
    const today = new Date();
    const todayKey = today.toISOString().slice(0, 10);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = yesterday.toISOString().slice(0, 10);

    const current = JSON.parse(localStorage.getItem(key) || '{"lastVisit":"","streak":0}');

    if (current.lastVisit === todayKey) {
      setStreak(current.streak || 1);
      return;
    }

    const nextStreak = current.lastVisit === yesterdayKey ? (current.streak || 0) + 1 : 1;

    localStorage.setItem(
      key,
      JSON.stringify({
        lastVisit: todayKey,
        streak: nextStreak
      })
    );
    setStreak(nextStreak);
  }, [user]);

  const sidebarItems = [
    { key: 'dashboard', label: 'Dashboard', icon: <FaHome />, action: () => navigate('/dashboard') },
    { key: 'my-courses', label: 'My Courses', icon: <FaBook />, action: () => navigate('/courses') },
    {
      key: 'certificates',
      label: 'Certificates',
      icon: <FaTrophy />,
      action: () => navigate('/dashboard/certificates')
    },
    { key: 'profile', label: 'Profile', icon: <FaUser />, action: () => navigate('/profile') },
    { key: 'browse', label: 'Browse', icon: <FaGraduationCap />, action: () => navigate('/courses') }
  ];

  if (loading) {
    return (
      <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center" style={{ color: 'var(--af-text)' }}>
          <FaSpinner className="fa-spin mb-3" size={40} />
          <h4>Loading Dashboard...</h4>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <main className="fundisha-dashboard">
      <div className="fundisha-dashboard-shell">
        <aside className="fundisha-dashboard-sidebar" aria-label="Dashboard sidebar navigation">
          {sidebarItems.map((item) => (
            <button
              key={item.key}
              className={`fundisha-sidebar-link ${activeNav === item.key ? 'active' : ''}`}
              type="button"
              onClick={() => {
                setActiveNav(item.key);
                item.action();
              }}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </aside>

        <section>
          <header className="fundisha-dashboard-welcome">
            <h1>Welcome back, {userDisplayName}.</h1>
            <div className="fundisha-streak">
              <FaFire />
              <span>{streak}-day streak</span>
            </div>
            <p className="fundisha-quote">
              "{quote.quote}" {quote.author ? `- ${quote.author}` : ''}
            </p>
          </header>

          <div className="fundisha-dash-stats">
            <article className="fundisha-dash-stat">
              <p className="mb-1" style={{ color: 'var(--af-text-muted)' }}>Enrolled</p>
              <h3>{enrolledCourses.length}</h3>
            </article>
            <article className="fundisha-dash-stat">
              <p className="mb-1" style={{ color: 'var(--af-text-muted)' }}>In Progress</p>
              <h3>{inProgressCourses.length}</h3>
            </article>
            <article className="fundisha-dash-stat">
              <p className="mb-1" style={{ color: 'var(--af-text-muted)' }}>Completed</p>
              <h3>{completedCourses.length}</h3>
            </article>
            <article className="fundisha-dash-stat">
              <p className="mb-1" style={{ color: 'var(--af-text-muted)' }}>Certificates</p>
              <h3>{completedCourses.length}</h3>
            </article>
          </div>

          {spotlightCourse && (
            <article className="fundisha-dash-spotlight">
              <div>
                <p style={{ color: 'var(--af-accent)', marginBottom: '0.35rem', textTransform: 'uppercase' }}>
                  Continue Learning
                </p>
                <h3 style={{ marginBottom: '0.35rem' }}>{spotlightCourse.title}</h3>
                <p style={{ marginBottom: 0, color: 'var(--af-text-muted)' }}>
                  Pick up where you left off and keep your momentum alive.
                </p>
              </div>
              <button
                className="fundisha-play-btn"
                type="button"
                aria-label="Continue last accessed course"
                onClick={() => navigate(`/courses/${spotlightCourse.id}/content`)}
              >
                <FaPlay />
              </button>
            </article>
          )}

          <section className="fundisha-progress-grid" aria-label="Course progress cards">
            {enrolledCourses.length > 0 ? (
              enrolledCourses.map((course) => (
                <article key={course.id} className="fundisha-progress-card">
                  <div>
                    <h4 style={{ marginBottom: '0.25rem' }}>{course.title}</h4>
                    <p style={{ marginBottom: 0, color: 'var(--af-text-muted)' }}>
                      {(course.instructor && course.instructor.name) || 'Fundisha Instructor'}
                    </p>
                  </div>
                  <ProgressRing progress={course.progress || 0} />
                </article>
              ))
            ) : (
              <article className="fundisha-progress-card">
                <div>
                  <h4 style={{ marginBottom: '0.3rem' }}>No active courses yet</h4>
                  <p style={{ marginBottom: 0, color: 'var(--af-text-muted)' }}>
                    Explore the catalog and enroll in your first course.
                  </p>
                </div>
                <button className="fundisha-btn-primary" type="button" onClick={() => navigate('/courses')}>
                  Browse Courses
                </button>
              </article>
            )}
          </section>
        </section>
      </div>

      <nav className="fundisha-bottom-tabs" aria-label="Mobile dashboard tabs">
        {sidebarItems.map((item) => (
          <button
            key={item.key}
            className={`fundisha-bottom-tab ${activeNav === item.key ? 'active' : ''}`}
            type="button"
            onClick={() => {
              setActiveNav(item.key);
              item.action();
            }}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
    </main>
  );
};

export default Dashboard;
